import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { Refresh, LaunchOutlined } from '@mui/icons-material';
import { BhiDialog as BHIDialog, Dropdown } from 'src/UIKit';
import { capitalizeFirstLetter } from 'src/helpers/helpers';
import { CompanyInfoTabViewModel } from 'src/modules/companies/types';
import { halfWidthFieldSx, halfWidthLeadingFieldSx } from 'src/modules/companies/helpers';

const CompanyInfoTabView = (props: CompanyInfoTabViewModel) => {
  const {
    loading,
    applications,
    formValues,
    initialValues,
    errors,
    timeZones,
    onboardingStatuses,
    onboardingStatusValue,
    isUpdatingOnboardingStatus,
    isOnboardingStatusDialogOpen,
    isReactivateInfoDialogOpen,
    assigningCampaign,
    refreshingStatus,
    refreshingCampaignStatus,
    reactivatingCampaign,
    assignmentTaskStatus,
    telnyxCampaignStatus,
    assignmentStatusFailure,
    telnyxCampaignFailure,
    hasTcrCampaignId,
    canReactivateTcrCampaign,
    hasFormChanged,
    isValidForm,
    onChange,
    onChangeApplication,
    onChangeTimezone,
    onChangeManualBilling,
    onChangeIsDemo,
    onChangeSandBoxMode,
    onChangeOnboardingStatus,
    onCancelOnboardingStatusUpdate,
    onConfirmOnboardingStatusUpdate,
    onSave,
    onRefreshAssignmentStatus,
    onRefreshTelnyxCampaignStatus,
    onAssignTcrCampaign,
    onReactivateTcrCampaign,
    onCloseReactivateInfoDialog,
  } = props;

  return (
    <Box mt={3.75}>
      {loading && <CircularProgress />}

      {!loading && applications && (
        <Stack spacing={2.5}>
          <Stack width="100%" gap={0.625} direction="row">
            <TextField
              sx={halfWidthFieldSx}
              id="outlined-basic"
              label={'Name'}
              name="name"
              variant="outlined"
              value={formValues.name || undefined}
              onChange={onChange}
              error={!!errors.name}
              helperText={errors?.name || ''}
            />
            <FormControl sx={{ width: '49%', ml: '1%' }}>
              <InputLabel>Application</InputLabel>
              <Select
                label="Application"
                MenuProps={{ disableScrollLock: true }}
                value={formValues.application.id}
                onChange={onChangeApplication}
                name="fieldName"
              >
                {applications
                  .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                  .map(c => (
                    <MenuItem value={c.id} key={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack width="100%" gap={0.625} direction="row">
            <TextField
              sx={halfWidthLeadingFieldSx}
              id="outlined-basic"
              label={'TCR Campaign ID'}
              name="tcrCampaignId"
              variant="outlined"
              value={formValues.tcrCampaignId || undefined}
              onChange={onChange}
              error={!!errors.tcrCampaignId}
              helperText={errors?.tcrCampaignId || ''}
              InputProps={{
                endAdornment: (
                  formValues.tcrCampaignId === initialValues.tcrCampaignId && formValues.tcrCampaignId && (
                    <Tooltip title="Assign TCR Campaign to Message Profile">
                      <IconButton
                        onClick={onAssignTcrCampaign}
                        disabled={assigningCampaign}
                        size="small"
                      >
                        {assigningCampaign ? <CircularProgress size={16} /> : <LaunchOutlined />}
                      </IconButton>
                    </Tooltip>
                  )
                ),
              }}
            />
            <TextField
              sx={halfWidthFieldSx}
              id="outlined-basic"
              label={'Assignment Status'}
              variant="outlined"
              value={capitalizeFirstLetter(assignmentTaskStatus.assignmentStatus) || 'N/A'}
              error={Boolean(assignmentStatusFailure)}
              helperText={assignmentStatusFailure || ''}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <>
                    <Tooltip title="Refresh Assignment Status">
                      <IconButton
                        onClick={onRefreshAssignmentStatus}
                        disabled={refreshingStatus}
                        size="small"
                      >
                        {refreshingStatus ? <CircularProgress size={16} /> : <Refresh />}
                      </IconButton>
                    </Tooltip>
                  </>
                ),
              }}
            />
          </Stack>

          <Stack width="100%" gap={0.625} direction="row">
            <TextField
              sx={halfWidthLeadingFieldSx}
              id="outlined-basic"
              label={'Message Profile ID'}
              name="messagingProfileId"
              variant="outlined"
              value={formValues.messagingProfileId || undefined}
              onChange={onChange}
              error={!!errors.messagingProfileId}
              helperText={errors?.messagingProfileId || ''}
            />
            <TextField
              sx={halfWidthFieldSx}
              id="telnyx-campaign-status"
              label={'Telnyx Campaign Status'}
              variant="outlined"
              value={ telnyxCampaignStatus.status ?? 'N/A' }
              error={Boolean(telnyxCampaignFailure)}
              helperText={telnyxCampaignFailure || ''}
              InputProps={{
                readOnly: true,
                endAdornment: hasTcrCampaignId ? (
                  <Stack direction="row" alignItems="center" gap={1}>
                    {canReactivateTcrCampaign && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={onReactivateTcrCampaign}
                        disabled={reactivatingCampaign || refreshingCampaignStatus}
                      >
                        {reactivatingCampaign ? <CircularProgress size={16} /> : 'Reactivate'}
                      </Button>
                    )}
                    <Tooltip title="Refresh Telnyx Campaign Status">
                      <IconButton
                        onClick={onRefreshTelnyxCampaignStatus}
                        disabled={refreshingCampaignStatus || reactivatingCampaign}
                        size="small"
                      >
                        {refreshingCampaignStatus ? <CircularProgress size={16} /> : <Refresh />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : undefined,
              }}
            />
          </Stack>

          <Stack width="100%" gap={0.625} direction="row">
            <FormControl sx={{ width: '100%', mr: '0.5%' }}>
              <InputLabel>Account Setup Status</InputLabel>
              <Select
                label="Account Setup Status"
                MenuProps={{ disableScrollLock: true }}
                value={onboardingStatusValue}
                onChange={onChangeOnboardingStatus}
                name="onboardingStatus"
                disabled={isUpdatingOnboardingStatus}
              >
                {onboardingStatuses.map(status => (
                  <MenuItem value={status} key={status}>
                    {capitalizeFirstLetter(status)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack width="100%" gap={0.625} direction="row">
            <FormControl sx={halfWidthLeadingFieldSx}>
              <InputLabel>Time zone</InputLabel>
              <Select
                label="Time zone"
                MenuProps={{ disableScrollLock: true }}
                value={formValues.timezone}
                onChange={onChangeTimezone}
                name="timezone"
              >
                {timeZones.map(item => {
                  return (
                    <MenuItem value={item} key={item}>
                      {item}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Dropdown
              label="Billing Type"
              value={formValues.manualBilling.toString()}
              options={[
                { title: 'Manual Billing', value: 'true' },
                { title: 'Automatic Billing', value: 'false' },
              ]}
              onChange={(_, v) => onChangeManualBilling(v)}
              margin="none"
              sx={halfWidthFieldSx}
            />
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <FormControlLabel
              sx={halfWidthLeadingFieldSx}
              control={
                <Checkbox
                  name="sandboxMode"
                  checked={formValues.sandBoxMode}
                  onChange={e => onChangeSandBoxMode(e)}
                />
              }
              label="Playground Mode"
            />
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <FormControlLabel
              sx={halfWidthLeadingFieldSx}
              control={
                <Checkbox
                  name="isDemo"
                  checked={formValues.isDemo}
                  onChange={e => onChangeIsDemo(e)}
                />
              }
              label="Demo Company"
            />
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Button
              size="small"
              disabled={!isValidForm(formValues) || !hasFormChanged()}
              variant="contained"
              sx={{ width: 150 }}
              onClick={onSave}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      )}

      <BHIDialog
        isOpen={isOnboardingStatusDialogOpen}
        onClose={isUpdatingOnboardingStatus ? () => undefined : onCancelOnboardingStatusUpdate}
        maxWidth="sm"
        title="Confirm Account Setup Status Change"
      >
        <DialogContent>
          Changing account setup status affects company behavior and should only be done when you fully understand
          the impact.
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={onCancelOnboardingStatusUpdate} disabled={isUpdatingOnboardingStatus}>
            Cancel
          </Button>
          <Button onClick={onConfirmOnboardingStatusUpdate} variant="contained" disabled={isUpdatingOnboardingStatus}>
            {isUpdatingOnboardingStatus ? <CircularProgress size={18} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </BHIDialog>

      <BHIDialog
        isOpen={isReactivateInfoDialogOpen}
        onClose={onCloseReactivateInfoDialog}
        maxWidth="sm"
        title="Reactivation In Progress"
      >
        <DialogContent>
          Reactivation can take 2-3 minutes. Please wait, refresh Telnyx campaign status, and only then decide
          whether a repeated reactivation attempt is necessary.
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCloseReactivateInfoDialog} variant="contained">
            OK
          </Button>
        </DialogActions>
      </BHIDialog>
    </Box>
  );
};

export default CompanyInfoTabView;
